import { clearRegisterPending, readRegisterPending } from './register-pending'

const isBrowser = typeof window !== 'undefined'

const KEY = {
  progress: 'cognito-register-progress',
  complete: 'cognito-register-complete',
  authError: 'cognito-register-error',
  email: 'signupEmail',
  formError: 'signupFormError',
  userId: 'userSub'
}

// A legacy key nothing writes any more; still cleared so an upgrade does not leave it behind.
const RETIRED_KEYS = ['domainStatus']

// Long enough to read an e-mail and click its link, short enough that an abandoned
// registration is not still being restored an hour later.
const MAX_AGE_MS = 30 * 60 * 1000

const readValue = (key) => {
  if (!isBrowser) return ''
  try {
    return window.sessionStorage.getItem(key) || ''
  } catch {
    return ''
  }
}

const writeValue = (key, value) => {
  if (!isBrowser) return
  try {
    if (typeof value === 'string' && value) window.sessionStorage.setItem(key, value)
    else window.sessionStorage.removeItem(key)
  } catch {
    /* no-op */
  }
}

const drop = (key) => writeValue(key, '')

/** Reads a timestamped record, clearing it rather than returning it once it has aged out. */
const readRecord = (key) => {
  if (!isBrowser) return null
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    if (typeof parsed.ts === 'number' && Date.now() - parsed.ts > MAX_AGE_MS) {
      drop(key)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const writeRecord = (key, record) => {
  if (!isBrowser) return
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ ...record, ts: Date.now() }))
  } catch {
    /* no-op */
  }
}

const asString = (value) => (typeof value === 'string' ? value : '')

export const readRegistrationState = () => {
  const progress = readRecord(KEY.progress)
  const complete = readRecord(KEY.complete)
  const pending = readRegisterPending()
  const authError = readRecord(KEY.authError)

  return {
    email: readValue(KEY.email) ||
      asString(progress?.email) ||
      asString(pending?.email) ||
      asString(complete?.email) ||
      asString(authError?.email),
    userId: readValue(KEY.userId) ||
      // `userSub` is the progress record's older name for the same value; a registration
      // started before this deploy can still be in storage under it.
      asString(progress?.userId) || asString(progress?.userSub) ||
      asString(pending?.userId),
    // The server's verdict on the domain, and the only reason the confirm step attempts the
    // grant. Seeded from the cookie as well, because on the emailed-link load it is the only
    // one of the two present.
    expectedInstitutional: progress?.expectedInstitutional === true || pending?.expectedInstitutional === true,
    startedHere: Boolean(progress),
    complete: Boolean(complete),
    formError: readValue(KEY.formError) || asString(authError?.message)
  }
}

export const storeRegistrationProgress = ({ email, userId, expectedInstitutional }) => {
  writeRecord(KEY.progress, { email: email || '', userId: userId || '', expectedInstitutional: expectedInstitutional === true })
  writeValue(KEY.email, email || '')
  writeValue(KEY.userId, userId || '')
}

export const clearRegistrationProgress = () => drop(KEY.progress)

export const readRegistrationEmail = () => readValue(KEY.email)
export const storeRegistrationEmail = (email) => writeValue(KEY.email, (email || '').trim())
export const storeRegistrationUserId = (userId) => writeValue(KEY.userId, userId || '')
export const storeRegistrationFormError = (message) => writeValue(KEY.formError, message || '')

/** Written by the auth layer for a form that has not mounted yet, so it survives a redirect. */
export const storeRegistrationAuthError = (message, email = '') => (
  writeRecord(KEY.authError, { message: message || '', email: email || '' })
)
export const clearRegistrationAuthError = () => drop(KEY.authError)

export const markRegistrationComplete = (email) => writeRecord(KEY.complete, { email: email || '' })
export const clearRegistrationComplete = () => drop(KEY.complete)

/** Clears the address and account id the flow was carrying, once it no longer needs them. */
export const clearRegistrationHandoff = () => {
  drop(KEY.email)
  drop(KEY.userId)
  clearRegisterPending()
}

/** Everything, cookie included - a sign-out must not leave a registration mid-flow behind. */
export const clearRegistrationState = () => {
  Object.values(KEY).forEach(drop)
  RETIRED_KEYS.forEach(drop)
  clearRegisterPending()
}
